import { useState, useEffect } from 'react';
import {
  HiOutlinePhone,
  HiOutlineClipboardList,
  HiOutlineBookOpen
} from 'react-icons/hi';
import { dashboardService } from '../api/services';
import toast from 'react-hot-toast';

function StatCard({ icon: Icon, title, value, subValue, subLabel, color }) {
  const colorClasses = {
    gold: 'bg-gold-500/10 border-gold-500/30 text-gold-500',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
    green: 'bg-green-500/10 border-green-500/30 text-green-500',
  };

  return (
    <div className="card hover-card group">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-100 mt-2">{value}</p>
          {subValue !== undefined && (
            <p className="text-sm text-gray-500 mt-1">
              <span className={`text-${color}-500`}>{subValue}</span> {subLabel}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl border ${colorClasses[color]} transition-transform group-hover:scale-110`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardService.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold gold-gradient-text">Dashboard</h1>
        <p className="text-gray-400 mt-1">Welcome to KG Luxury Admin Panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={HiOutlineBookOpen}
          title="Catalogues"
          value={stats?.catalogues?.total || 0}
          subValue={stats?.catalogues?.active || 0}
          subLabel="active"
          color="gold"
        />
        <StatCard
          icon={HiOutlinePhone}
          title="Contacts"
          value={stats?.contacts?.total || 0}
          subValue={stats?.contacts?.new || 0}
          subLabel="new"
          color="green"
        />
        <StatCard
          icon={HiOutlineClipboardList}
          title="Enquiries"
          value={stats?.enquiries?.total || 0}
          subValue={stats?.enquiries?.new || 0}
          subLabel="new"
          color="blue"
        />
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gold-500 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/catalogues"
            className="p-4 bg-dark-500 rounded-lg border border-dark-400 hover:border-gold-500/50 transition-colors"
          >
            <HiOutlineBookOpen className="w-8 h-8 text-gold-500 mb-2" />
            <h3 className="font-medium text-gray-200">Manage Catalogues</h3>
            <p className="text-sm text-gray-500">Add or edit catalogues</p>
          </a>
          <a
            href="/contacts"
            className="p-4 bg-dark-500 rounded-lg border border-dark-400 hover:border-gold-500/50 transition-colors"
          >
            <HiOutlinePhone className="w-8 h-8 text-gold-500 mb-2" />
            <h3 className="font-medium text-gray-200">View Contacts</h3>
            <p className="text-sm text-gray-500">Contact form submissions</p>
          </a>
          <a
            href="/enquiries"
            className="p-4 bg-dark-500 rounded-lg border border-dark-400 hover:border-gold-500/50 transition-colors"
          >
            <HiOutlineClipboardList className="w-8 h-8 text-gold-500 mb-2" />
            <h3 className="font-medium text-gray-200">View Enquiries</h3>
            <p className="text-sm text-gray-500">Catalogue enquiries</p>
          </a>
        </div>
      </div>
    </div>
  );
}
